import { type EngineContext } from '../core/EngineContext';
import { type EngineEventMap } from '../core/EngineEvents';
import { type Unsubscribe } from '../utils/Unsubscribe';
import { type Entity } from './Entity';
import { type EntityId } from './EntityId';

/** What every engine event about one particular entity carries. */
interface EntityScopedPayload {
  readonly entityId: EntityId;
}

/** The engine events that name the single entity they concern, and so can be filtered by id. */
export type EntityScopedEventName = {
  [TName in keyof EngineEventMap]: EngineEventMap[TName] extends EntityScopedPayload ? TName : never;
}[keyof EngineEventMap];

/**
 * The optional lifecycle hooks a behavior may implement.
 *
 * Declared as an interface merged into the class below so subclasses implement only the hooks they
 * need, with no empty overrides and no `super` calls to forget. Because they merge into the class,
 * `noImplicitOverride` requires the `override` keyword on each one: `override onUpdate(dt) { ... }`.
 */
export interface Behavior {
  /** Called once, after the behavior is attached and {@link Behavior.entity} is available. */
  onAttach?(entity: Entity, context: EngineContext): void;
  /** Called once per frame while the owning entity is active, in attach order. */
  onUpdate?(deltaSeconds: number): void;
  /** Called when the behavior is removed or its entity is destroyed. Release anything acquired here. */
  onDetach?(): void;
}

/**
 * Gameplay logic bound to one entity.
 *
 * Behaviors are the only place per-entity logic belongs: components hold data, systems handle anything
 * that must see every entity of a kind, and a behavior handles what one entity does. Configuration
 * passed to a behavior's constructor follows the component rule and stays plain serializable data.
 *
 * Reach the wider engine through {@link Behavior.context}; behaviors never import services directly.
 *
 * @example
 * ```ts
 * class Spin extends Behavior {
 *   readonly #radiansPerSecond: number;
 *
 *   constructor(radiansPerSecond = 1) {
 *     super();
 *     this.#radiansPerSecond = radiansPerSecond;
 *   }
 *
 *   override onUpdate(deltaSeconds: number): void {
 *     this.entity.transform.rotation += this.#radiansPerSecond * deltaSeconds;
 *   }
 * }
 * ```
 */
export abstract class Behavior {
  readonly #subscriptions: Unsubscribe[] = [];

  #entity: Entity | undefined;
  #context: EngineContext | undefined;

  /** Whether this behavior is currently attached to an entity. */
  get isAttached(): boolean {
    return this.#entity !== undefined;
  }

  /** The owning entity. Throws when read before attach or after detach. */
  get entity(): Entity {
    if (this.#entity === undefined) {
      throw new Error(`${this.constructor.name} is not attached to an entity.`);
    }
    return this.#entity;
  }

  /** Engine services for this behavior's world. Throws when read before attach or after detach. */
  get context(): EngineContext {
    if (this.#context === undefined) {
      throw new Error(`${this.constructor.name} is not attached to an entity.`);
    }
    return this.#context;
  }

  /**
   * Listens to an engine event for as long as this behavior stays attached.
   *
   * Detaching drops the subscription, so a behavior that only needs to react to an event writes no
   * `onDetach` at all. Forgetting that teardown is the one mistake with real consequences: a listener
   * that outlives its entity keeps firing for whatever is given that id next.
   */
  protected subscribe<TName extends keyof EngineEventMap>(
    name: TName,
    listener: (payload: EngineEventMap[TName]) => void,
  ): void {
    this.#subscriptions.push(this.context.events.on(name, listener));
  }

  /** Listens to an event about one entity, ignoring every occurrence that names a different one. */
  protected subscribeForEntity<TName extends EntityScopedEventName>(
    name: TName,
    listener: (payload: EngineEventMap[TName]) => void,
  ): void {
    const ownId = this.entity.id;
    this.subscribe(name, (payload) => {
      if (payload.entityId === ownId) {
        listener(payload);
      }
    });
  }

  /**
   * Binds the behavior to an entity and runs {@link Behavior.onAttach}.
   *
   * @internal Called by {@link Entity.addBehavior}; not part of the public API.
   */
  attach(entity: Entity, context: EngineContext): void {
    if (this.#entity !== undefined) {
      throw new Error(`${this.constructor.name} is already attached to entity "${this.#entity.name}".`);
    }
    this.#entity = entity;
    this.#context = context;
    this.onAttach?.(entity, context);
  }

  /**
   * Runs {@link Behavior.onDetach} and unbinds the behavior.
   *
   * @internal Called by {@link Entity}; not part of the public API.
   */
  detach(): void {
    if (this.#entity === undefined) {
      return;
    }
    this.onDetach?.();
    for (const unsubscribe of this.#subscriptions) {
      unsubscribe();
    }
    this.#subscriptions.length = 0;
    this.#entity = undefined;
    this.#context = undefined;
  }
}

/** The constructor of a behavior class, used as the lookup key for `entity.getBehavior(...)`. */
export type BehaviorType<TBehavior extends Behavior = Behavior> = abstract new (...args: never[]) => TBehavior;
